import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Money, Spinner, StatusBadge, EmptyState, fmtMoney, ChartTooltip, tooltipChrome, axisDenom, denomTick, CCY_SYMBOL } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Info, X, ChevronRight } from "lucide-react";

const COLORS = ["#064E3B", "#C59B27", "#0B192C", "#059669", "#D97706", "#475569"];

function cleanParams(obj) {
  const o = {};
  Object.entries(obj || {}).forEach(([k, v]) => { if (v) o[k] = v; });
  return o;
}

function useReport(report, ccy, filters) {
  const [d, setD] = useState(null);
  const qs = useMemo(() => new URLSearchParams({ reporting_ccy: ccy, ...cleanParams(filters) }).toString(), [ccy, filters]);
  useEffect(() => { setD(null); api.get(`/mis/${report}?${qs}`).then((r) => setD(r.data)).catch(() => setD({ __error: true })); }, [report, qs]);
  return d;
}

function useFilterOptions() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/mis/filter-options").then((r) => setO(r.data)).catch(() => setO({})); }, []);
  return o;
}

function Definition({ text, ccy, date }) {
  return <div className="flex items-start gap-2 text-xs text-muted-foreground mb-4 bg-secondary rounded-lg p-3"><Info className="h-4 w-4 shrink-0 mt-0.5 text-gold" /><span>{text} {ccy && `· Reporting currency ${ccy}`} {date && `· Valuation ${date}`} · Source freshness T-1 (mock).</span></div>;
}

function FxNote({ fx, asOf }) {
  if (!fx) return null;
  const others = Object.entries(fx.rates || {}).filter(([c]) => c !== fx.reporting_ccy);
  return (
    <div className="text-xs text-muted-foreground mb-4 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="mis-fx-note">
      <span className="font-medium text-slate-700 dark:text-slate-300">Reporting currency {fx.reporting_ccy}</span>
      <span>· Exchange rates @ {fx.fx_date}</span>
      {asOf && <span>· As-of {asOf}</span>}
      <span className="text-muted-foreground/80">{others.map(([c, r]) => `1 ${c} = ${r} ${fx.reporting_ccy}`).join("  ·  ")}</span>
    </div>
  );
}

function Dropdown({ label, value, onChange, options, testid }) {
  return (
    <div className="flex flex-col gap-1 min-w-[150px]">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger data-testid={testid} className="h-9"><SelectValue placeholder={`All ${label}`} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All {label}</SelectItem>
          {(options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function DateField({ label, value, onChange, testid }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 w-[150px]" data-testid={testid} />
    </div>
  );
}

function emptyFilters(mode) {
  return mode === "income"
    ? { division: "", rm: "", customer_id: "", product: "", date_from: "", date_to: "" }
    : { division: "", rm: "", customer_id: "", product: "", as_of: "" };
}

function FilterBar({ opts, filters, set, mode }) {
  const upd = (k, v) => set({ ...filters, [k]: v });
  const customersOpts = (opts?.customers || []).map((c) => ({ value: c.id, label: `${c.name} (${c.code})` }));
  const products = mode === "income" ? (opts?.income_products || []) : (opts?.asset_products || []);
  const active = Object.values(filters).some((v) => v);
  return (
    <Card className="p-4 mb-4" data-testid="mis-filter-bar">
      <div className="flex flex-wrap items-end gap-3">
        <Dropdown label="Division" value={filters.division} onChange={(v) => upd("division", v)} options={(opts?.divisions || []).map((d) => ({ value: d, label: d }))} testid="mis-filter-division" />
        <Dropdown label="Manager" value={filters.rm} onChange={(v) => upd("rm", v)} options={(opts?.rms || []).map((d) => ({ value: d, label: d }))} testid="mis-filter-rm" />
        <Dropdown label="Customer" value={filters.customer_id} onChange={(v) => upd("customer_id", v)} options={customersOpts} testid="mis-filter-customer" />
        <Dropdown label="Product" value={filters.product} onChange={(v) => upd("product", v)} options={products.map((p) => ({ value: p, label: p.replace(/_/g, " ") }))} testid="mis-filter-product" />
        {mode === "assets" && <DateField label="As-of date" value={filters.as_of} onChange={(v) => upd("as_of", v)} testid="mis-filter-asof" />}
        {mode === "income" && <DateField label="From" value={filters.date_from} onChange={(v) => upd("date_from", v)} testid="mis-filter-from" />}
        {mode === "income" && <DateField label="To" value={filters.date_to} onChange={(v) => upd("date_to", v)} testid="mis-filter-to" />}
        <Button variant="outline" size="sm" disabled={!active} onClick={() => set(emptyFilters(mode))} data-testid="mis-filter-clear"><X className="h-3.5 w-3.5 mr-1" />Clear filters</Button>
      </div>
    </Card>
  );
}

function BarFromObj({ obj, ccy, money }) {
  const data = Object.entries(obj || {}).map(([name, value]) => ({ name: name.replace(/_/g, " "), value }));
  if (data.length === 0) return <EmptyState title="No data" />;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} layout="vertical" margin={{ left: 40 }}>
        <XAxis type="number" tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
        <Tooltip content={<ChartTooltip ccy={ccy} money={money} />} {...tooltipChrome} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]}>{data.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function ClickableBar({ title, obj, ccy, money, onPick, activeKey, testid }) {
  const data = Object.entries(obj || {}).map(([name, value]) => ({ name, label: name.replace(/_/g, " "), value }));
  const denom = money ? axisDenom(Math.max(0, ...data.map((d) => Math.abs(d.value))), ccy) : null;
  const unitLabel = money && denom && denom.suffix ? ` (${(CCY_SYMBOL[ccy] || ccy).trim()}${denom.suffix})` : money ? ` (${ccy})` : "";
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">{title}<span className="text-muted-foreground font-normal">{unitLabel}</span></h3>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Click to filter</span>
      </div>
      {data.length === 0 ? <EmptyState title="No data for current filters" /> : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} layout="vertical" margin={{ left: 40 }}>
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={money && denom ? (v) => denomTick(v, denom) : undefined} />
            <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} />
            <Tooltip content={<ChartTooltip ccy={ccy} money={money} />} cursor={{ fill: "rgba(6,78,59,0.05)" }} {...tooltipChrome} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d) => onPick && onPick(d && d.name)}>
              {data.map((e, i) => <Cell key={i} fill={e.name === activeKey ? "#C59B27" : COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

export default function MIS() {
  const { reportingCcy, user } = useAuth();
  const opts = useFilterOptions();
  if (user?.role === "customer") {
    return (
      <div>
        <PageHeader overline="Analytics" title="Management Information" testid="mis-header" />
        <EmptyState title="Access denied" hint="Management Information is available to staff roles only." />
      </div>
    );
  }
  return (
    <div>
      <PageHeader overline="Analytics" title="Management Information" subtitle="Dashboards with drill-down and filters. Totals reconcile with underlying records." testid="mis-header" />
      <Tabs defaultValue="assets">
        <TabsList className="flex-wrap h-auto">
          {["assets", "income", "onboarding", "compliance", "leads", "service-requests", "reconciliation"].map((t) =>
            <TabsTrigger key={t} value={t} data-testid={`mis-tab-${t}`} className="capitalize">{t.replace(/-/g, " ")}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="assets" className="mt-4"><Assets ccy={reportingCcy} opts={opts} /></TabsContent>
        <TabsContent value="income" className="mt-4"><IncomeR ccy={reportingCcy} opts={opts} /></TabsContent>
        <TabsContent value="onboarding" className="mt-4"><Onboarding ccy={reportingCcy} /></TabsContent>
        <TabsContent value="compliance" className="mt-4"><ComplianceR ccy={reportingCcy} /></TabsContent>
        <TabsContent value="leads" className="mt-4"><LeadsR ccy={reportingCcy} /></TabsContent>
        <TabsContent value="service-requests" className="mt-4"><SRR ccy={reportingCcy} /></TabsContent>
        <TabsContent value="reconciliation" className="mt-4"><ReconR ccy={reportingCcy} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Assets({ ccy, opts }) {
  const [filters, setFilters] = useState(emptyFilters("assets"));
  const d = useReport("assets", ccy, filters);
  const pickCustomer = (name) => {
    const c = (opts?.customers || []).find((x) => x.name === name);
    if (c) setFilters((f) => ({ ...f, customer_id: c.id }));
  };
  const activeCustomerName = (opts?.customers || []).find((x) => x.id === filters.customer_id)?.name;
  return (
    <div>
      <Definition text={d?.definition || "Investment AUM with drill-down."} ccy={ccy} />
      <FilterBar opts={opts} filters={filters} set={setFilters} mode="assets" />
      {!d ? <Spinner /> : (
        <>
          <FxNote fx={d.fx} asOf={d.as_of} />
          <div className="grid lg:grid-cols-2 gap-4">
            <ClickableBar title="By division" obj={d.by_division} ccy={ccy} money onPick={(n) => setFilters((f) => ({ ...f, division: n }))} activeKey={filters.division} testid="mis-chart-division" />
            <ClickableBar title="By relationship manager" obj={d.by_rm} ccy={ccy} money onPick={(n) => setFilters((f) => ({ ...f, rm: n }))} activeKey={filters.rm} testid="mis-chart-rm" />
            <ClickableBar title="By customer" obj={d.by_customer} ccy={ccy} money onPick={pickCustomer} activeKey={activeCustomerName} testid="mis-chart-customer" />
            <ClickableBar title="By product" obj={d.by_product} ccy={ccy} money onPick={(n) => setFilters((f) => ({ ...f, product: n }))} activeKey={filters.product} testid="mis-chart-product" />
          </div>
          <Card className="p-5 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Holdings ({d.count})</h3>
              <span className="text-xs text-muted-foreground">Individual records for the current filters</span>
            </div>
            {d.count === 0 ? <EmptyState title="No holdings match these filters" hint="Adjust or clear the filters above." /> : (
              <div className="overflow-x-auto">
                <Table data-testid="mis-assets-table">
                  <TableHeader><TableRow>
                    <TableHead>Customer</TableHead><TableHead>Division</TableHead><TableHead>Manager</TableHead>
                    <TableHead>Product</TableHead><TableHead>Scheme / Security</TableHead><TableHead>Folio</TableHead>
                    <TableHead className="text-right">Units</TableHead><TableHead className="text-right">Original value</TableHead>
                    <TableHead className="text-right">Value ({ccy})</TableHead><TableHead>Source</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {d.rows.map((r) => (
                      <TableRow key={r.id} data-testid="mis-assets-row">
                        <TableCell className="font-medium">{r.customer}</TableCell>
                        <TableCell>{r.division}</TableCell><TableCell>{r.rm}</TableCell>
                        <TableCell className="capitalize">{(r.product || "").replace(/_/g, " ")}</TableCell>
                        <TableCell>{r.scheme}</TableCell>
                        <TableCell className="font-num text-xs">{r.folio || "—"}</TableCell>
                        <TableCell className="text-right font-num">{r.units?.toLocaleString() ?? "—"}</TableCell>
                        <TableCell className="text-right font-num">{fmtMoney(r.value_original, r.currency)}</TableCell>
                        <TableCell className="text-right font-num font-semibold">{fmtMoney(r.value_reporting, ccy)}</TableCell>
                        <TableCell className="text-xs">{r.source}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="flex justify-between mt-4 pt-3 border-t border-border">
              <span className="font-semibold">Total AUM (reconciles with the records above)</span>
              <span className="font-num font-bold text-xl" data-testid="mis-assets-total"><Money value={d.total} ccy={ccy} /></span>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function IncomeR({ ccy, opts }) {
  const [filters, setFilters] = useState(emptyFilters("income"));
  const [kind, setKind] = useState("all"); // all | received | accrued
  const d = useReport("income", ccy, filters);
  const rows = (d?.rows || []).filter((r) => kind === "all" || r.income_type === kind);
  const shownTotal = rows.reduce((s, r) => s + (r.amount_reporting || 0), 0);
  return (
    <div>
      <Definition text={d?.definition || "Income received and accrued with drill-down."} ccy={ccy} />
      <FilterBar opts={opts} filters={filters} set={setFilters} mode="income" />
      {!d ? <Spinner /> : (
        <>
          <FxNote fx={d.fx} />
          <div className="grid grid-cols-3 gap-4 mb-4">
            <button onClick={() => setKind(kind === "received" ? "all" : "received")} data-testid="mis-income-received-card" className="text-left">
              <Card className={`p-5 transition-colors ${kind === "received" ? "ring-2 ring-gold" : "hover:bg-secondary/50"}`}>
                <div className="overline-label text-muted-foreground">Received</div>
                <div className="font-num font-bold text-2xl mt-2" data-testid="mis-income-received"><Money value={d.received} ccy={ccy} /></div>
                <div className="text-[11px] text-muted-foreground mt-1">Click to view received records</div>
              </Card>
            </button>
            <button onClick={() => setKind(kind === "accrued" ? "all" : "accrued")} data-testid="mis-income-accrued-card" className="text-left">
              <Card className={`p-5 transition-colors ${kind === "accrued" ? "ring-2 ring-gold" : "hover:bg-secondary/50"}`}>
                <div className="overline-label text-muted-foreground">Accrued</div>
                <div className="font-num font-bold text-2xl mt-2" data-testid="mis-income-accrued"><Money value={d.accrued} ccy={ccy} /></div>
                <div className="text-[11px] text-muted-foreground mt-1">Click to view accrued records</div>
              </Card>
            </button>
            <Card className="p-5 flex flex-col justify-center">
              <div className="overline-label text-muted-foreground">Total income</div>
              <div className="font-num font-bold text-2xl mt-2"><Money value={(d.received || 0) + (d.accrued || 0)} ccy={ccy} /></div>
              <div className="text-[11px] text-muted-foreground mt-1">Received + accrued</div>
            </Card>
          </div>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-semibold flex items-center gap-1">Income records ({rows.length})
                {kind !== "all" && <span className="inline-flex items-center text-xs text-gold ml-1"><ChevronRight className="h-3 w-3" />{kind}</span>}
              </h3>
              {kind !== "all" && <Button variant="outline" size="sm" onClick={() => setKind("all")} data-testid="mis-income-showall"><X className="h-3.5 w-3.5 mr-1" />Show all</Button>}
            </div>
            {rows.length === 0 ? <EmptyState title="No income records match these filters" hint="Adjust or clear the filters above." /> : (
              <div className="overflow-x-auto">
                <Table data-testid="mis-income-table">
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Product</TableHead>
                    <TableHead>Type</TableHead><TableHead>Bucket</TableHead>
                    <TableHead className="text-right">Original amount</TableHead>
                    <TableHead className="text-right">Amount ({ccy})</TableHead><TableHead>Source</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id} data-testid="mis-income-row">
                        <TableCell className="font-num text-xs">{r.date}</TableCell>
                        <TableCell className="font-medium">{r.customer}</TableCell>
                        <TableCell>{r.product}</TableCell>
                        <TableCell>{r.type}</TableCell>
                        <TableCell><StatusBadge status={r.income_type === "received" ? "completed" : "pending"} /></TableCell>
                        <TableCell className="text-right font-num">{fmtMoney(r.amount_original, r.currency)}</TableCell>
                        <TableCell className="text-right font-num font-semibold">{fmtMoney(r.amount_reporting, ccy)}</TableCell>
                        <TableCell className="text-xs">{r.source}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="flex justify-between mt-4 pt-3 border-t border-border">
              <span className="font-semibold">{kind === "all" ? "Total income shown" : `Total ${kind} shown`} (reconciles with the records above)</span>
              <span className="font-num font-bold text-lg" data-testid="mis-income-shown-total"><Money value={shownTotal} ccy={ccy} /></span>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Onboarding({ ccy }) {
  const d = useReport("onboarding", ccy, {}); if (!d) return <Spinner />;
  return (
    <div>
      <Definition text={d.definition} />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5"><h3 className="font-semibold mb-2">By status</h3><BarFromObj obj={d.by_status} /></Card>
        <Card className="p-5"><h3 className="font-semibold mb-3">Applications (drill-down)</h3>
          <Table><TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Step</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{d.rows.map((r) => <TableRow key={r.id}><TableCell>{r.applicant}</TableCell><TableCell className="capitalize">{r.current_step}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell></TableRow>)}</TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}

function ComplianceR({ ccy }) {
  const d = useReport("compliance", ccy, {}); if (!d) return <Spinner />;
  return (
    <div><Definition text={d.definition} />
      <Card className="p-5"><h3 className="font-semibold mb-3">Review queue ({d.count})</h3>
        <Table><TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Status</TableHead><TableHead>Risk</TableHead><TableHead>Screening</TableHead></TableRow></TableHeader>
          <TableBody>{d.rows.map((r) => <TableRow key={r.id}><TableCell>{r.applicant}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell><TableCell>{r.risk || "—"}</TableCell><TableCell>{r.screening ? <StatusBadge status={r.screening} /> : "—"}</TableCell></TableRow>)}</TableBody>
        </Table>
      </Card>
    </div>
  );
}

function LeadsR({ ccy }) {
  const d = useReport("leads", ccy, {}); if (!d) return <Spinner />;
  return (
    <div><Definition text={d.definition} />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5"><h3 className="font-semibold mb-2">By stage</h3><BarFromObj obj={d.by_stage} /></Card>
        <Card className="p-5 flex flex-col justify-center gap-3">
          <div className="flex justify-between"><span className="text-muted-foreground">Total leads</span><span className="font-num font-bold">{d.total}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Converted</span><span className="font-num font-bold">{d.converted}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Conversion</span><span className="font-num font-bold text-emerald-600" data-testid="mis-conversion">{d.conversion_pct}%</span></div>
        </Card>
      </div>
    </div>
  );
}

function SRR({ ccy }) {
  const d = useReport("service-requests", ccy, {}); if (!d) return <Spinner />;
  return (
    <div><Definition text={d.definition} />
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Card className="p-5"><h3 className="font-semibold mb-2">By status</h3><BarFromObj obj={d.by_status} /></Card>
        <Card className="p-5"><h3 className="font-semibold mb-2">By priority</h3><BarFromObj obj={d.by_priority} /></Card>
      </div>
      <Card className="p-5"><Table><TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Category</TableHead><TableHead>Customer</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Target</TableHead></TableRow></TableHeader>
        <TableBody>{d.rows.map((r) => <TableRow key={r.ref}><TableCell className="font-num text-xs">{r.ref}</TableCell><TableCell>{r.category}</TableCell><TableCell>{r.customer}</TableCell><TableCell className="capitalize">{r.priority}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell><TableCell className="font-num text-xs">{r.target_date}</TableCell></TableRow>)}</TableBody>
      </Table></Card>
    </div>
  );
}

function ReconR({ ccy }) {
  const d = useReport("reconciliation", ccy, {}); if (!d) return <Spinner />;
  return (
    <div><Definition text={d.definition} />
      <Card className="p-5"><Table><TableHeader><TableRow><TableHead>Run</TableHead><TableHead>Matched</TableHead><TableHead>Exceptions</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>{d.rows.map((r) => <TableRow key={r.id}><TableCell>{r.name}</TableCell><TableCell className="font-num text-emerald-600">{r.matched}</TableCell><TableCell className="font-num text-red-600">{r.exceptions}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell></TableRow>)}</TableBody>
      </Table>
      <div className="flex justify-between mt-3 pt-3 border-t border-border"><span className="font-semibold">Total exceptions (reconciles with Dashboard)</span><span className="font-num font-bold" data-testid="mis-recon-total">{d.total_exceptions}</span></div>
      </Card>
    </div>
  );
}
